import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  IsLoadingUserProfile,
  SelectActualUser,
  SelectActualUserEmail,
  SelectActualUserRoles,
  SelectCostCentersOwned,
  SelectCostCentersOwnedIds,
  setActualUser,
  setActualUserEmail,
  setActualUserRoles,
  setCostCentersOwned,
  setCostCentersOwnedIds,
  setIsLoadingUserProfile,
} from "../redux/reducers/usersList.reducer";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import {
  canSeeAllUsers,
  isSupervisor,
  isUserAdminOrSuperAdmin,
} from "../../SharedModule/utils/accessUtils";
import { UsersService } from "../services/users";
import { SecurityService } from "../../SharedModule/services/security";
import { addElementToArray } from "../../TimesModule/utils/timesUtils";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import { useNavigate } from "react-router";
import { ROUTE_USERS } from "../../SharedModule/utils/constants";
import { selectMetaTimesData } from "../../TimesModule/redux/reducers/metaTimes.reducer";
import { useTimesMetadata } from "../../TimesModule/hooks/useTimesMetadata";

const loadCostCentersIfSupervisor = async (
  isSupervisorInfo,
  isNotAdminOrViewer,
  costCentersOwnedIdsReducer,
  costCentersOwned,
  activeUserEmail,
  dispatch
) => {
  let costCentersOwnedIds: number[] = [];
  if (!isSupervisorInfo || !isNotAdminOrViewer || costCentersOwnedIdsReducer) {
    return costCentersOwnedIds;
  }
  dispatch(setIsLoadingUserProfile(true));
  let plainCostCenters: any[] = [];
  if (costCentersOwned) {
    addElementToArray(plainCostCenters, costCentersOwned);
    costCentersOwnedIds = plainCostCenters.map((elem) => elem.value);
    dispatch(setCostCentersOwnedIds(costCentersOwnedIds));
  } else {
    const costCentersOwnedResponse = await UsersService.getCostCentersOwned(
      activeUserEmail
    );
    dispatch(setCostCentersOwned(costCentersOwnedResponse));
    addElementToArray(plainCostCenters, costCentersOwnedResponse);
    costCentersOwnedIds = plainCostCenters.map((elem) => elem.value);
    dispatch(setCostCentersOwnedIds(costCentersOwnedIds));
  }
  return costCentersOwnedIds;
};

const loadUserAndRoles = async (
  actualUser,
  id,
  timeUsers,
  actualUserEmail,
  costCentersOwnedIds,
  isSupervisorInfo,
  isNotAdminOrViewer,
  dispatch,
  navigate
) => {
  if (!actualUser) {
    // if id is null
    if (!id) {
      dispatch(
        addToast({
          mode: "error",
          message: "User not found",
        })
      );
      navigate(ROUTE_USERS);
      return;
    }
    dispatch(setIsLoadingUserProfile(true));
    const response = await UsersService.getUsersWithProfileInfo(id);

    // if user not found
    if (!response || response.length === 0) {
      dispatch(
        addToast({
          mode: "error",
          message: "User not found",
        })
      );
      navigate(ROUTE_USERS);
      dispatch(setIsLoadingUserProfile(false));
      return;
    }

    // check if user cc is on list of cc ids
    if (
      isSupervisorInfo &&
      isNotAdminOrViewer &&
      response[0].costCenter &&
      !costCentersOwnedIds.includes(response[0].costCenter.number)
    ) {
      dispatch(
        addToast({
          mode: "error",
          message: "You're not allowed to see this user",
        })
      );
      navigate(ROUTE_USERS);
      dispatch(setIsLoadingUserProfile(false));
      return;
    }

    dispatch(
      setActualUser({
        actualUserData: response[0],
        allUsers: timeUsers,
        loadSupervisorName: true,
      })
    );
    dispatch(setActualUserEmail(response[0].email));

    const rolesResponse = await SecurityService.getUserRoles(
      response[0].email
    );
    dispatch(setActualUserRoles(rolesResponse));
    // when changes user from navigation need to reload roles
  } else if (actualUserEmail !== actualUser.email) {
    dispatch(setActualUserEmail(actualUser.email));
    const rolesResponse = await SecurityService.getUserRoles(
      actualUser.email
    );
    dispatch(setActualUserRoles(rolesResponse));
  }
};

export const useUserProfile = (id: number) => {
  useTimesMetadata();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userRolesList, rolesLoaded, activeUserEmail } =
    useSelector(selectUserRoles);
  const userAdminOrSuperAdmin = isUserAdminOrSuperAdmin(userRolesList);
  const actualUser = useSelector(SelectActualUser);
  const isLoadingUserProfile = useSelector(IsLoadingUserProfile);

  const actualUserEmail = useSelector(SelectActualUserEmail);
  const actualUserRoles = useSelector(SelectActualUserRoles);

  const isSupervisorInfo = isSupervisor(userRolesList);
  const isNotAdminOrViewer = !canSeeAllUsers(userRolesList);
  const costCentersOwned = useSelector(SelectCostCentersOwned);
  const costCentersOwnedIdsReducer = useSelector(SelectCostCentersOwnedIds);

  const { timeUsers, timesDataLoaded } = useSelector(selectMetaTimesData);

  const loadUserProfile = useCallback(async () => {
    if (!rolesLoaded || !timesDataLoaded) {
      return;
    }
    const costCentersOwnedIds = await loadCostCentersIfSupervisor(
      isSupervisorInfo,
      isNotAdminOrViewer,
      costCentersOwnedIdsReducer,
      costCentersOwned,
      activeUserEmail,
      dispatch
    );
    await loadUserAndRoles(
      actualUser,
      id,
      timeUsers,
      actualUserEmail,
      costCentersOwnedIds,
      isSupervisorInfo,
      isNotAdminOrViewer,
      dispatch,
      navigate
    );
  }, [rolesLoaded, actualUser, timesDataLoaded]);

  useEffect(() => {
    loadUserProfile();
  }, [loadUserProfile]);

  return {
    userRolesList,
    rolesLoaded,
    activeUserEmail,
    userAdminOrSuperAdmin,
    actualUser,
    isLoadingUserProfile,
    actualUserRoles,
  };
};
